import Api from '@/services/Api';
import { ALL_LANGUAGES } from '@/js/constants';

const TAB_OWNED_DRAFT_INVOICES_KEY =
  'samplesRegister_tabOwnedDraftInvoices';

function canUseSessionStorage() {
  return typeof window !== 'undefined' && !!window.sessionStorage;
}

function readTabOwnedDraftInvoices() {
  if (!canUseSessionStorage()) return [];

  try {
    const raw = window.sessionStorage.getItem(
      TAB_OWNED_DRAFT_INVOICES_KEY
    );
    const parsed = JSON.parse(raw || '[]');

    return Array.isArray(parsed)
      ? parsed
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value))
      : [];
  } catch {
    return [];
  }
}

function writeTabOwnedDraftInvoices(invoiceIds) {
  if (!canUseSessionStorage()) return;

  const normalizedIds = Array.from(
    new Set(
      (Array.isArray(invoiceIds) ? invoiceIds : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
    )
  );

  window.sessionStorage.setItem(
    TAB_OWNED_DRAFT_INVOICES_KEY,
    JSON.stringify(normalizedIds)
  );
}

function rememberTabOwnedDraftInvoice(invoiceId) {
  const normalizedId = Number(invoiceId);
  if (!Number.isFinite(normalizedId)) return;

  const invoiceIds = readTabOwnedDraftInvoices();
  invoiceIds.push(normalizedId);
  writeTabOwnedDraftInvoices(invoiceIds);
}

function forgetTabOwnedDraftInvoice(invoiceId) {
  const normalizedId = Number(invoiceId);
  if (!Number.isFinite(normalizedId)) return;

  const nextInvoiceIds = readTabOwnedDraftInvoices().filter(
    (value) => value !== normalizedId
  );
  writeTabOwnedDraftInvoices(nextInvoiceIds);
}

function isTabOwnedDraftInvoice(invoiceId) {
  const normalizedId = Number(invoiceId);
  if (!Number.isFinite(normalizedId)) return false;

  return readTabOwnedDraftInvoices().includes(normalizedId);
}

function syncTabOwnedDraftInvoicesWithServer(invoices) {
  const validInvoiceIds = new Set(
    (Array.isArray(invoices) ? invoices : [])
      .map((invoice) => Number(invoice?.idAlbaran))
      .filter((value) => Number.isFinite(value))
  );

  const nextOwnedIds = readTabOwnedDraftInvoices().filter((invoiceId) =>
    validInvoiceIds.has(invoiceId)
  );

  writeTabOwnedDraftInvoices(nextOwnedIds);
}

function getLanguage(rootState) {
  const userLang =
    rootState.user?.authenticatedUser?.language_code ||
    rootState.user?.authenticatedUser?.contacts?.[0]?.language;

  if (
    userLang &&
    ALL_LANGUAGES.some((lang) => lang.key === userLang)
  ) {
    return userLang;
  }

  // Si tuviera language_code a NULL por no tener teléfono, se aplica el idioma por defecto según su sede
  const companyCode =
    rootState.user?.authenticatedUser?.contacts?.[0]?.companies?.[0]
      ?.code;
  if (companyCode) {
    const countryCode = companyCode.substring(0, 2);
    const countryLangMap = {
      ES: 'SPA',
      CL: 'SPA',
      PE: 'SPA',
      AR: 'SPA',
      ME: 'SPA',
      CR: 'SPA',
      CO: 'SPA',
      EC: 'SPA',
      RD: 'SPA',
      US: 'ENG',
      PO: 'POR',
      IT: 'ITA',
    };
    if (countryLangMap[countryCode]) {
      return countryLangMap[countryCode];
    }
  }

  return 'SPA';
}

function getInvoiceSamplesCount(invoice) {
  const parsedCount = Number(invoice?.NumMuestras);
  return Number.isFinite(parsedCount) ? parsedCount : 0;
}

function isPendingInvoice(invoice) {
  return (
    invoice?.Estado === 'Sin Enviar' ||
    Number(invoice?.idEstado) === 100
  );
}

export default {
  namespaced: true,
  state: {
    offerSelected: '',
    sampletypes: [],
    families: [],
    legislations: [],
    farmsWithParcels: [],
    confirmPopup: false,
    confirmExpressSamplePopUp: false,
    confirmDeliveryPopup: false,
    confirmTemplatesAvailablePopup: false,
    agencies: [],
    invoice: [],
    showSplPopup: false,
    splPopupTitle: '',
    invoiceSamples: [],
    invoiceDetails: null,
    detailedSample: [],
    invoiceState: '',
    invoiceCode: '',
    lmrCountries: [],
    registeredSamples: [],
    registeredInvoice: [],
    sampleCodeInfoPopUp: [],
    invoiceCodeInfoPopUp: [],
    idInvoice: [],
    deletedInvoice: [],
    deletedSample: [],
    updatedSample: [],
    updatedInvoice: [],
    invoicePDF: [],
    priceAlert: false,
    SamplingDate: '',
    expressRegisterCache: {
      budgetCode: null,
      analysisType: [],
      familiesFiltered: [],
      offersFamiliesFiltered: [],
    },
    expressRegisterBudgetCacheByCode: {},
    errorPopup: false,
    errorMessage: '',
  },
  actions: {
    async fetchSamplesTypes({ commit, rootState }, payload) {
      try {
        const language = getLanguage(rootState);
        const requestData =
          payload &&
          typeof payload === 'object' &&
          !Array.isArray(payload)
            ? payload
            : { IdFamilia: payload };
        const xhr = await Api.getSamplesType(
          requestData.IdFamilia,
          language,
          requestData.subfamilias || [],
          requestData.IdSubFamilia || ''
        );
        const response = JSON.parse(xhr.response);
        commit('setSampleTypes', response.tipoMuestras);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async registerInvoiceSamples({ commit, rootState }, data) {
      try {
        const buildBasePayload = (d) => {
          // En modo express enviamos una cabecera comun y despues
          // fusionamos cada fila de MuestrasExpress para reutilizar
          // el mismo endpoint de alta unitaria sin cambiar contrato.
          const { MuestrasExpress, isExpressMode, ...base } = d;
          return {
            ...base,
            CodCliente: rootState.contact.company,
          };
        };
        if (data.isExpressMode) {
          // El renombrado a "express" solo afecta al frontend:
          // seguimos registrando una muestra por llamada a API y luego
          // agregamos las respuestas para mantener la funcionalidad.
          const basePayload = buildBasePayload(data);
          const promises = (data.MuestrasExpress || []).map(
            (muestra) => {
              const payload = {
                ...basePayload,
                ...muestra,
              };

              return Api.registerSample(payload);
            }
          );
          const responses = await Promise.all(promises);
          const registeredSamples = responses.map((xhr) =>
            JSON.parse(xhr.response)
          );
          commit('setRegisteredSamples', registeredSamples);
          commit(
            'setSampleCodeInfoPopUp',
            registeredSamples
              .map((s) => s?.CodMuestra)
              .filter(Boolean)
          );
          return Promise.resolve(registeredSamples);
        } else {
          const payload = buildBasePayload(data);
          const xhr = await Api.registerSample(payload);
          const response = JSON.parse(xhr.response);
          commit('setRegisteredSamples', response);
          commit('setSampleCodeInfoPopUp', response?.CodMuestra);
          return Promise.resolve(response);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async RegisterInvoice({ commit, rootState }) {
      try {
        const xhr = await Api.RegisterInvoice(
          rootState.contact.company
        );
        const response = JSON.parse(xhr.response);
        rememberTabOwnedDraftInvoice(response.idAlbaran);
        commit('setRegisteredInvoice', response);
        commit('setInvoiceCodeInfoPopUp', response.CodAlbaran);
        commit('setIdInvoice', response.idAlbaran);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async deleteInvoice({ commit }, payload) {
      try {
        const requestData =
          payload &&
          typeof payload === 'object' &&
          !Array.isArray(payload)
            ? payload
            : { idInvoice: payload, deleteSource: null };
        const { idInvoice, deleteSource } = requestData;

        if (
          idInvoice === null ||
          idInvoice === undefined ||
          idInvoice === '' ||
          Array.isArray(idInvoice)
        ) {
          return Promise.resolve();
        }
        const xhr = await Api.deleteInvoice(idInvoice, deleteSource);
        const response = JSON.parse(xhr.response);
        forgetTabOwnedDraftInvoice(idInvoice);
        commit('setDeletedInvoice', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async updateInvoice({ commit }, data) {
      try {
        const xhr = await Api.updateInvoice(data);
        const response = JSON.parse(xhr.response);
        commit('setUpdatedInvoice', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async generateInvoicePDF({ commit, rootState }, idInvoice) {
      try {
        const languageKey = getLanguage(rootState);

        // Mapeo de language al valor esperado en la API AGQ
        const ALL_LANGUAGES = [
          { key: 'ENG', value: 'en-US' },
          { key: 'SPA', value: 'es-ES' },
          { key: 'FRA', value: 'fr-FR' },
          { key: 'POR', value: 'pt-PT' },
          { key: 'ITA', value: 'it-IT' },
        ];

        // Buscar el objeto cuyo key coincida
        const match = ALL_LANGUAGES.find(
          (l) => l.key === languageKey
        );
        const codIdioma = match ? match.value : 'es-ES'; // por defecto en español

        const xhr = await Api.invoicePDFGenerator(
          rootState.contact.company,
          idInvoice,
          codIdioma
        );
        const response = JSON.parse(xhr.response);
        commit('setInvoicePDF', response);
        if (response.nombre && response.documentoBase64) {
          const link = document.createElement('a');
          link.href = `data:application/pdf;base64,${response.documentoBase64}`;
          link.download = response.nombre;
          link.click();

          URL.revokeObjectURL(link.href);
        }
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async deleteSample({ commit }, idSample) {
      try {
        const xhr = await Api.deleteSample(idSample);
        const response = JSON.parse(xhr.response);
        commit('setDeletedSample', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async updateSample({ commit }, data) {
      try {
        const xhr = await Api.updateSample(data);
        const response = JSON.parse(xhr.response);
        commit('setUpdatedSample', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchFamiliesByHierarchy({ commit, rootState }) {
      try {
        const language = getLanguage(rootState);
        const xhr = await Api.getFamilies(language);
        const response = JSON.parse(xhr.response);
        commit('setFamilies', response.familiasTM);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchLegislations({ commit }) {
      try {
        const xhr = await Api.getLegislations();
        const response = JSON.parse(xhr.response);
        commit('setLegislations', response.legislaciones);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchFarmsAndParcels({ commit, rootState }) {
      try {
        const xhr = await Api.getFarmsAndParcels(
          rootState.contact.company
        );
        const response = JSON.parse(xhr.response);
        commit('setFarmsWithParcels', response.fincas);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchAgencies({ commit, rootState }) {
      try {
        const codpais = rootState.contact.company.substring(0, 2);
        const xhr = await Api.getAgencies(codpais);
        const response = JSON.parse(xhr.response);
        commit('setAgencies', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchAlbaranes(
      { commit, rootState, dispatch },
      statusFilter
    ) {
      try {
        const xhr = await Api.getInvoices(
          rootState.contact.company,
          statusFilter
        );
        const response = JSON.parse(xhr.response);

        if (!Array.isArray(response)) {
          commit('setInvoice', response);
          return Promise.resolve();
        }

        const visibleInvoices = response.filter(
          (invoice) => getInvoiceSamplesCount(invoice) > 0
        );
        const emptyPendingInvoices = response.filter(
          (invoice) =>
            getInvoiceSamplesCount(invoice) < 1 &&
            isPendingInvoice(invoice) &&
            invoice?.idAlbaran &&
            isTabOwnedDraftInvoice(invoice.idAlbaran)
        );

        syncTabOwnedDraftInvoicesWithServer(response);
        commit('setInvoice', visibleInvoices);

        if (emptyPendingInvoices.length > 0) {
          await Promise.allSettled(
            emptyPendingInvoices.map((invoice) =>
              dispatch('deleteInvoice', {
                idInvoice: invoice.idAlbaran,
                deleteSource: 'auto_cleanup_empty_draft',
              })
            )
          );
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchInvoiceSamples({ commit, rootState }, idInvoice) {
      try {
        const xhr = await Api.getInvoiceSamples(
          rootState.contact.company,
          idInvoice
        );
        const response = JSON.parse(xhr.response);
        commit('setInvoiceSamples', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setSampleTypes({ commit }, value) {
      commit('setSampleTypes', value);
    },
    setFamilies({ commit }, value) {
      commit('setFamilies', value);
    },
    setOfferSelected({ commit }, value) {
      commit('setOfferSelected', value);
    },
    setLegislations({ commit }, value) {
      commit('setLegislations', value);
    },
    setFarmsWithParcels({ commit }, value) {
      commit('setFarmsWithParcels', value);
    },
    setAgencies({ commit }, value) {
      commit('setAgencies', value);
    },
    setConfirmPopup({ commit }, value) {
      commit('setConfirmPopup', value);
    },
    setConfirmExpressSamplePopUp({ commit }, value) {
      commit('setConfirmExpressSamplePopUp', value);
    },
    setConfirmDeliveryPopup({ commit }, value) {
      commit('setConfirmDeliveryPopup', value);
    },
    setConfirmTemplatesAvailablePopup({ commit }, value) {
      commit('setConfirmTemplatesAvailablePopup', value);
    },
    setInvoice({ commit }, value) {
      commit('setInvoice', value);
    },
    setSplpopup({ commit }, value) {
      commit('setSplpopup', value);
    },
    setSplPopupTitle({ commit }, value) {
      commit('setSplPopupTitle', value);
    },
    setInvoiceSamples({ commit }, value) {
      commit('setInvoiceSamples', value);
    },
    setInvoiceDetails({ commit }, value) {
      commit('setInvoiceDetails', value);
    },
    setDetailedSample({ commit }, value) {
      commit('setDetailedSample', value);
    },
    setInvoiceState({ commit }, value) {
      commit('setInvoiceState', value);
    },
    setInvoiceCode({ commit }, value) {
      commit('setInvoiceCode', value);
    },
    setLmrCountries({ commit }, value) {
      commit('setLmrCountries', value);
    },
    setRegisteredSamples({ commit }, value) {
      commit('setRegisteredSamples', value);
    },
    setRegisteredInvoice({ commit }, value) {
      commit('setRegisteredInvoice', value);
    },
    setSampleCodeInfoPopUp({ commit }, value) {
      commit('setSampleCodeInfoPopUp', value);
    },
    setInvoiceCodeInfoPopUp({ commit }, value) {
      commit('setInvoiceCodeInfoPopUp', value);
    },
    setIdInvoice({ commit }, value) {
      commit('setIdInvoice', value);
    },
    setDeletedInvoice({ commit }, value) {
      commit('setDeletedInvoice', value);
    },
    setDeletedSample({ commit }, value) {
      commit('setDeletedSample', value);
    },
    setUpdatedSample({ commit }, value) {
      commit('setUpdatedSample', value);
    },
    setUpdatedInvoice({ commit }, value) {
      commit('setUpdatedInvoice', value);
    },
    setInvoicePDF({ commit }, value) {
      commit('setInvoicePDF', value);
    },
    setPriceAlert({ commit }, value) {
      commit('setPriceAlert', value);
    },
    setSamplingDate({ commit }, value) {
      commit('setSamplingDate', value);
    },
    setExpressRegisterCache({ commit }, value) {
      commit('setExpressRegisterCache', value);
    },
    clearExpressRegisterCache({ commit }) {
      commit('clearExpressRegisterCache');
    },
    setExpressRegisterBudgetCacheByCode({ commit }, value) {
      commit('setExpressRegisterBudgetCacheByCode', value);
    },
    resetData({ commit }) {
      commit('resetData');
    },
    setErrorPopup({ commit }, value) {
      commit('setErrorPopup', value);
    },
  },
  mutations: {
    setSampleTypes(state, value) {
      state.sampletypes = value;
    },
    setFamilies(state, value) {
      state.families = value;
    },
    setOfferSelected(state, value) {
      state.offerSelected = value;
    },
    setLegislations(state, value) {
      state.legislations = value;
    },
    setFarmsWithParcels(state, value) {
      state.farmsWithParcels = value;
    },
    setAgencies(state, value) {
      state.agencies = value;
    },
    setConfirmPopup(state, value) {
      state.confirmPopup = value;
    },
    setConfirmExpressSamplePopUp(state, value) {
      state.confirmExpressSamplePopUp = value;
    },
    setConfirmDeliveryPopup(state, value) {
      state.confirmDeliveryPopup = value;
    },
    setConfirmTemplatesAvailablePopup(state, value) {
      state.confirmTemplatesAvailablePopup = value;
    },
    setInvoice(state, value) {
      state.invoice = value;
    },
    setSplpopup(state, value) {
      state.showSplPopup = value;
    },
    setSplPopupTitle(state, value) {
      state.splPopupTitle = value;
    },
    setInvoiceSamples(state, value) {
      state.invoiceSamples = value;
    },
    setInvoiceDetails(state, value) {
      state.invoiceDetails = value;
    },
    setDetailedSample(state, value) {
      state.detailedSample = value;
    },
    setInvoiceState(state, value) {
      state.invoiceState = value;
    },
    setInvoiceCode(state, value) {
      state.invoiceCode = value;
    },
    setLmrCountries(state, value) {
      state.lmrCountries = value;
    },
    setRegisteredSamples(state, value) {
      state.registeredSamples = value;
    },
    setRegisteredInvoice(state, value) {
      state.registeredInvoice = value;
    },
    setSampleCodeInfoPopUp(state, value) {
      state.sampleCodeInfoPopUp = value;
    },
    setInvoiceCodeInfoPopUp(state, value) {
      state.invoiceCodeInfoPopUp = value;
    },
    setIdInvoice(state, value) {
      state.idInvoice = value;
    },
    setDeletedInvoice(state, value) {
      state.deletedInvoice = value;
    },
    setDeletedSample(state, value) {
      state.deletedSample = value;
    },
    setUpdatedSample(state, value) {
      state.updatedSample = value;
    },
    setUpdatedInvoice(state, value) {
      state.updatedInvoice = value;
    },
    setInvoicePDF(state, value) {
      state.invoicePDF = value;
    },
    setPriceAlert(state, value) {
      state.priceAlert = value;
    },
    setSamplingDate(state, value) {
      state.SamplingDate = value;
    },
    setExpressRegisterCache(state, value) {
      state.expressRegisterCache = {
        budgetCode: value?.budgetCode || null,
        analysisType: Array.isArray(value?.analysisType)
          ? value.analysisType
          : [],
        familiesFiltered: Array.isArray(value?.familiesFiltered)
          ? value.familiesFiltered
          : [],
        offersFamiliesFiltered: Array.isArray(
          value?.offersFamiliesFiltered
        )
          ? value.offersFamiliesFiltered
          : [],
      };
    },
    clearExpressRegisterCache(state) {
      state.expressRegisterCache = {
        budgetCode: null,
        analysisType: [],
        familiesFiltered: [],
        offersFamiliesFiltered: [],
      };
    },
    setExpressRegisterBudgetCacheByCode(state, value) {
      state.expressRegisterBudgetCacheByCode = {
        ...(state.expressRegisterBudgetCacheByCode || {}),
        ...(value || {}),
      };
    },
    resetData(state) {
      state.sampletypes = [];
      state.families = [];
      state.offerSelected = '';
      state.legislations = [];
      state.farmsWithParcels = [];
      state.confirmPopup = false;
      state.confirmExpressSamplePopUp = false;
      state.confirmDeliveryPopup = false;
      state.confirmTemplatesAvailablePopup = false;
      state.agencies = [];
      state.invoice = [];
      state.showSplPopup = false;
      state.splPopupTitle = '';
      state.invoiceSamples = [];
      state.invoiceDetails = null;
      state.showInvoiceInfo = false;
      state.invoiceInfo = [];
      state.detailedSample = [];
      state.invoiceState = '';
      state.lmrCountries = [];
      state.registeredSamples = [];
      state.registeredInvoice = [];
      state.sampleCodeInfoPopUp = [];
      state.invoiceCodeInfoPopUp = [];
      state.idInvoice = [];
      state.deletedInvoice = [];
      state.deletedSample = [];
      state.updatedSample = [];
      state.updatedInvoice = [];
      state.invoicePDF = [];
      state.priceAlert = false;
      state.SamplingDate = '';
      state.expressRegisterCache = {
        budgetCode: null,
        analysisType: [],
        familiesFiltered: [],
        offersFamiliesFiltered: [],
      };
      state.expressRegisterBudgetCacheByCode = {};
    },
    setErrorPopup(state, value) {
      state.errorPopup = value.visible;
      state.errorMessage = value.message || '';
    },
  },
};
